import { useEffect, useRef, useState } from 'react';

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Número que sube (o baja) hasta el valor final; sin animación si el usuario la reduce. */
export function useCountUp(target: number | null, duration = 700): number | null {
  const [value, setValue] = useState<number | null>(target);
  const previous = useRef<number>(target ?? 0);
  useEffect(() => {
    if (target == null) {
      setValue(null);
      return undefined;
    }
    if (prefersReducedMotion()) {
      previous.current = target;
      setValue(target);
      return undefined;
    }
    const from = previous.current;
    const start = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - progress) ** 3;
      const next = from + (target - from) * eased;
      previous.current = next;
      setValue(next);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration]);
  return value;
}

/** Valor que arranca en 0 y pasa a `target` tras el primer pintado, para animar una transición CSS. */
export function useGrowIn(target: number | null): number {
  const [value, setValue] = useState(0);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setValue(target ?? 0));
    return () => cancelAnimationFrame(frame);
  }, [target]);
  return value;
}

/** Keyframes compartidos por las barras, el panel de relleno y la escena. */
export const MOTION_STYLES = `
@keyframes sv-shine{from{transform:translateX(-120%)}to{transform:translateX(320%)}}
@keyframes sv-rise{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
@keyframes sv-pulse{0%,100%{opacity:.55}50%{opacity:1}}
.sv-fill{position:relative;overflow:hidden;transition:width .9s cubic-bezier(.2,.8,.2,1)}
.sv-fill::after{content:'';position:absolute;inset:0;width:40%;background:linear-gradient(90deg,transparent,rgba(255,255,255,.45),transparent);animation:sv-shine 2.6s ease-in-out infinite}
.sv-rise{animation:sv-rise .45s ease backwards}
.sv-pulse{animation:sv-pulse 2.4s ease-in-out infinite}
@keyframes sv-growx{from{transform:scaleX(0)}to{transform:scaleX(1)}}
@keyframes sv-pop{from{opacity:0;transform:scale(.6)}to{opacity:1;transform:scale(1)}}
.sv-growx{transform-box:fill-box;transform-origin:left center;animation:sv-growx .9s cubic-bezier(.2,.8,.2,1) backwards}
.sv-pop{transform-box:fill-box;transform-origin:center bottom;animation:sv-pop .42s cubic-bezier(.2,.9,.3,1.2) backwards}
.sv-tile{cursor:pointer;transition:filter .2s ease,opacity .25s ease}
.sv-tile:hover{filter:brightness(1.12)}
@media (prefers-reduced-motion:reduce){.sv-fill{transition:none}.sv-fill::after,.sv-rise,.sv-pulse,.sv-growx,.sv-pop{animation:none}}
`;
